/**
 * Practice areas — the law-firm spine of the site.
 *
 * Single source of truth shared by the homepage grid and /practice, and by the
 * per-area pages planned for the next lot. Hand-authored, deterministic, no AI
 * call and no credit spend.
 *
 * Content rules enforced here:
 *  - no prices, no fee figures, no "starting from"
 *  - no outcome guarantee (Lawyers Council rules + Google policy)
 *  - no unverifiable statistics, ratings or testimonials
 *  - every "we handle it for you" line stays factual: scope of work, not results
 */

export interface PracticeArea {
  slug: string;
  /** Short label used in cards and nav. */
  name: string;
  /** One-line positioning shown under the card title. */
  tagline: string;
  /** 2-3 sentence overview for the hub page. */
  intro: string;
  /** What the firm actually does in this area. */
  scope: string[];
  /** Concrete matters clients bring us — helps searchers self-identify. */
  examples: string[];
  /** First-pass document checklist. */
  docs: string[];
  /** Working sequence, plain language. */
  steps: string[];
  /** Mistakes we see most often. */
  cautions: { title: string; body: string }[];
  /** Persuasion block: why handing the whole matter to the firm is easier. */
  handoff: string;
  /** Existing site pages that go deeper. */
  links: { label: string; to: string }[];
}

export const PRACTICE_AREAS: PracticeArea[] = [
  {
    slug: "litigation",
    name: "คดีความและว่าความทุกศาล",
    tagline: "ฟ้อง ต่อสู้คดี อุทธรณ์ ฎีกา และบังคับคดีจนจบกระบวนการ",
    intro:
      "รับว่าความในศาลแพ่ง ศาลอาญา ศาลแรงงาน ศาลภาษีอากร ศาลทรัพย์สินทางปัญญาและการค้าระหว่างประเทศ ศาลล้มละลาย และศาลปกครอง ตั้งแต่ประเมินรูปคดี เจรจาก่อนฟ้อง ยื่นฟ้องหรือยื่นคำให้การ สืบพยาน ไปจนถึงชั้นอุทธรณ์ ฎีกา และบังคับคดีตามคำพิพากษา ทีมงานเปิดสำนวนให้ลูกความหนึ่งชุด มีทนายผู้รับผิดชอบชัดเจน และรายงานความคืบหน้าทุกนัดของศาล",
    scope: [
      "ประเมินรูปคดี พยานหลักฐาน และอายุความก่อนตัดสินใจฟ้อง",
      "หนังสือบอกกล่าว ทวงถาม และการเจรจาไกล่เกลี่ยก่อนฟ้อง",
      "ยื่นฟ้อง ยื่นคำให้การ ฟ้องแย้ง คำร้องคุ้มครองชั่วคราว",
      "ว่าความ สืบพยาน และดำเนินกระบวนพิจารณาแทนลูกความ",
      "อุทธรณ์ ฎีกา และคำร้องขอพิจารณาคดีใหม่",
      "ยึด อายัด ขายทอดตลาด และสืบทรัพย์ในชั้นบังคับคดี",
    ],
    examples: [
      "ผิดสัญญาซื้อขาย รับเหมาก่อสร้าง หรือสัญญาบริการ",
      "เรียกคืนเงินกู้ ค้ำประกัน จำนอง และหนี้การค้า",
      "ละเมิด เรียกค่าเสียหายจากอุบัติเหตุหรือความเสียหายต่อทรัพย์สิน",
      "คดีเช็ค ฉ้อโกง ยักยอก และคดีอาญาที่เกี่ยวกับธุรกิจ",
      "ข้อพิพาทแรงงาน เลิกจ้างไม่เป็นธรรม และค่าชดเชย",
      "บังคับคดีตามคำพิพากษาที่ลูกหนี้ไม่ชำระ",
    ],
    docs: [
      "สัญญาหรือหลักฐานนิติสัมพันธ์ทั้งหมดที่มี",
      "หลักฐานการชำระเงิน สลิป ใบเสร็จ ใบแจ้งหนี้",
      "ข้อความสนทนา อีเมล หนังสือโต้ตอบระหว่างคู่กรณี",
      "บัตรประชาชนหรือหนังสือรับรองนิติบุคคลของฝ่ายเรา",
      "ข้อมูลคู่กรณี ที่อยู่ตามทะเบียน และทรัพย์สินที่ทราบ",
      "เอกสารคดีเดิม หมายศาล หรือคำฟ้องที่ได้รับ (ถ้ามี)",
    ],
    steps: [
      "ส่งเอกสารและเล่าเรื่องให้ทีมงานประเมินรูปคดีเบื้องต้น",
      "ทนายสรุปแนวทาง ทางเลือก ความเสี่ยง และค่าบริการเป็นลายลักษณ์อักษร",
      "ลงนามใบแต่งทนายและมอบอำนาจ พร้อมรวบรวมพยานหลักฐาน",
      "เจรจาหรือยื่นฟ้อง/ยื่นคำให้การภายในกรอบอายุความ",
      "ว่าความ สืบพยาน และรายงานผลทุกนัดพิจารณา",
      "ดำเนินการชั้นอุทธรณ์ ฎีกา หรือบังคับคดีตามผลที่เกิดขึ้น",
    ],
    cautions: [
      {
        title: "อายุความคือเรื่องที่แก้ย้อนหลังไม่ได้",
        body: "คดีแต่ละประเภทมีอายุความไม่เท่ากัน หากปล่อยเวลาผ่านไปสิทธิเรียกร้องอาจสิ้นไปทั้งที่มีหลักฐานครบ ควรให้ทนายตรวจกำหนดเวลาให้เร็วที่สุด",
      },
      {
        title: "หมายศาลที่ไม่ตอบภายในกำหนด",
        body: "การไม่ยื่นคำให้การภายในเวลาที่กฎหมายกำหนดอาจทำให้ถูกพิจารณาโดยขาดนัด นำเอกสารมาให้ทนายทันทีที่ได้รับหมาย",
      },
      {
        title: "ประนีประนอมด้วยตัวเองโดยไม่ตรวจถ้อยคำ",
        body: "บันทึกข้อตกลงหรือข้อความยอมรับหนี้ที่เขียนเองอาจปิดสิทธิของตนเองในภายหลัง ควรให้ทนายตรวจก่อนลงนามทุกครั้ง",
      },
      {
        title: "หลักฐานเสียหายหรือหาย",
        body: "ภาพถ่าย ข้อความ และเอกสารต้นฉบับควรเก็บรวบรวมทันที ระบบแชตบางระบบลบข้อความอัตโนมัติและกู้คืนภายหลังไม่ได้",
      },
    ],
    handoff:
      "ถ้าไม่อยากยุ่งยากกับการเดินศาล จัดเรียงเอกสาร หรือคุยกับคู่กรณีเอง ให้สำนักงานรับดูแลทั้งสำนวนแทน ทนายผู้รับผิดชอบจะประเมินรูปคดี ยื่นเอกสารต่อศาล เข้าทุกนัดพิจารณา และรายงานผลให้ท่านทราบ ท่านเข้ามาเฉพาะนัดที่ศาลกำหนดให้ตัวความมาเท่านั้น",
    links: [
      { label: "บริการคดีความและว่าความ", to: "/litigation" },
      { label: "คำถามที่พบบ่อยเรื่องคดีความ", to: "/litigation/questions" },
    ],
  },
  {
    slug: "corporate",
    name: "ธุรกิจ บริษัท สัญญา และ M&A",
    tagline: "ตั้งบริษัท ร่างสัญญา ตรวจสอบกิจการ ซื้อขายหุ้น และงานกำกับดูแล",
    intro:
      "ดูแลงานฝั่งองค์กรตั้งแต่จดทะเบียนตั้งบริษัท เปลี่ยนแปลงกรรมการและทุน ขอใบอนุญาตประกอบธุรกิจ ร่างและตรวจสัญญาทางการค้า ไปจนถึงงานตรวจสอบสถานะกิจการ (Due Diligence) และโครงสร้างการซื้อขายหุ้นหรือกิจการ ทำงานร่วมกับฝ่ายบัญชีและภาษีในสำนักงานเดียว จึงตรวจได้ทั้งมุมกฎหมายและมุมภาระภาษีไปพร้อมกัน",
    scope: [
      "จดทะเบียนห้างหุ้นส่วน บริษัทจำกัด และบริษัทที่มีผู้ถือหุ้นต่างชาติ",
      "แก้ไขวัตถุที่ประสงค์ กรรมการ ที่ตั้ง ทุนจดทะเบียน และข้อบังคับ",
      "ร่างและตรวจสัญญาการค้า ตัวแทนจำหน่าย จ้างทำของ เช่า และ NDA",
      "สัญญาผู้ถือหุ้น สัญญาซื้อขายหุ้น และโครงสร้างการร่วมทุน",
      "ตรวจสอบสถานะกิจการก่อนลงทุนหรือซื้อกิจการ",
      "งานกำกับดูแล ใบอนุญาตประกอบธุรกิจ และเอกสารประชุมผู้ถือหุ้น",
    ],
    examples: [
      "ชาวต่างชาติต้องการร่วมทุนตั้งบริษัทในไทยอย่างถูกต้อง",
      "คู่ค้าเสนอสัญญามาให้เซ็น ต้องการให้ทนายตรวจข้อเสียเปรียบ",
      "ผู้ถือหุ้นขัดแย้งกัน ต้องการวางกลไกตัดสินใจใหม่",
      "ซื้อกิจการร้านอาหารหรือโรงงาน ต้องตรวจหนี้และใบอนุญาตก่อนโอน",
      "ต้องการเอกสารบริษัทฉบับแปลและรับรองเพื่อใช้ต่างประเทศ",
    ],
    docs: [
      "หนังสือรับรองบริษัทและบัญชีรายชื่อผู้ถือหุ้นฉบับล่าสุด",
      "ข้อบังคับบริษัทและรายงานการประชุมที่เกี่ยวข้อง",
      "ร่างสัญญาหรือสัญญาฉบับที่ใช้อยู่",
      "งบการเงินและแบบยื่นภาษีย้อนหลังตามที่ทนายแจ้ง",
      "สำเนาบัตรประชาชนหรือหนังสือเดินทางของกรรมการและผู้ถือหุ้น",
      "ใบอนุญาตประกอบธุรกิจหรือใบอนุญาตเฉพาะกิจการ",
    ],
    steps: [
      "แจ้งเป้าหมายทางธุรกิจและส่งเอกสารบริษัทให้ทีมงานตรวจ",
      "ทนายสรุปโครงสร้างที่ทำได้ตามกฎหมายพร้อมข้อจำกัดและค่าบริการ",
      "ร่างเอกสาร สัญญา หรือมติที่ประชุมให้ตรวจแก้ร่วมกัน",
      "ลงนามและยื่นต่อกรมพัฒนาธุรกิจการค้าหรือหน่วยงานที่เกี่ยวข้อง",
      "รับเอกสารจดทะเบียน และจัดชุดสำเนา/แปล/รับรองตามที่ต้องใช้",
      "วางระบบเอกสารและปฏิทินหน้าที่ตามกฎหมายให้ปีต่อไป",
    ],
    cautions: [
      {
        title: "โครงสร้างผู้ถือหุ้นที่เลี่ยงกฎหมาย",
        body: "การใช้ผู้ถือหุ้นแทน (นอมินี) เพื่อเลี่ยงข้อจำกัดของคนต่างชาติมีความผิดตามกฎหมาย สำนักงานไม่รับดำเนินการ และจะเสนอโครงสร้างที่ทำได้จริงตามกฎหมายให้แทน",
      },
      {
        title: "เซ็นสัญญาโดยไม่ตรวจข้อระงับข้อพิพาท",
        body: "ข้อกำหนดเรื่องศาลที่มีอำนาจ กฎหมายที่ใช้บังคับ และการอนุญาโตตุลาการ กระทบต้นทุนอย่างมากเมื่อเกิดข้อพิพาท ควรตรวจก่อนลงนาม",
      },
      {
        title: "เอกสารบริษัทไม่ตรงกับความเป็นจริง",
        body: "กรรมการหรือที่ตั้งที่ไม่ได้แก้ไขให้เป็นปัจจุบัน ทำให้เอกสารถูกปฏิเสธและอาจมีความรับผิดตามกฎหมาย",
      },
      {
        title: "ไม่ตรวจสถานะกิจการก่อนโอน",
        body: "หนี้ ภาระผูกพัน คดีค้าง และใบอนุญาตที่โอนไม่ได้ มักพบหลังชำระเงินแล้วซึ่งแก้ไขยาก",
      },
    ],
    handoff:
      "ถ้าไม่อยากเดินเอกสารและตีความข้อกฎหมายเอง มอบให้สำนักงานดูแลทั้งชุด ทั้งร่างเอกสาร ยื่นจดทะเบียน ประสานหน่วยงาน และจัดเอกสารฉบับแปลรับรองสำหรับใช้ต่างประเทศ ท่านเพียงตรวจร่างและลงนาม",
    links: [
      { label: "จดทะเบียนบริษัทและงานนิติบุคคล", to: "/corporate" },
      { label: "ตรวจสอบสถานะกิจการ (Due Diligence)", to: "/due-diligence" },
    ],
  },
  {
    slug: "family",
    name: "ครอบครัว",
    tagline: "สมรส หย่า อำนาจปกครอง สินสมรส และคดีครอบครัวระหว่างประเทศ",
    intro:
      "งานครอบครัวเป็นเรื่องที่กระทบทั้งสิทธิและความรู้สึกของครอบครัว สำนักงานดูแลทั้งงานจดทะเบียนและงานคดี ตั้งแต่จดทะเบียนสมรสของคู่สมรสไทย-ต่างชาติ สัญญาก่อนสมรส หย่าโดยความยินยอมหรือโดยคำพิพากษา อำนาจปกครองและค่าอุปการะเลี้ยงดูบุตร การรับรองบุตร ไปจนถึงการแบ่งสินสมรสและเอกสารที่ต้องใช้ยื่นต่อหน่วยงานต่างประเทศ",
    scope: [
      "จดทะเบียนสมรสไทย–ต่างชาติ และเอกสารรับรองสถานะบุคคล",
      "สัญญาก่อนสมรสและข้อตกลงเกี่ยวกับทรัพย์สินระหว่างคู่สมรส",
      "หย่าโดยความยินยอมและฟ้องหย่าต่อศาล",
      "อำนาจปกครองบุตร ค่าอุปการะเลี้ยงดู และสิทธิเยี่ยมเยียน",
      "รับรองบุตร รับบุตรบุญธรรม และคดีสถานะบุคคล",
      "แบ่งสินสมรสและติดตามทรัพย์สินที่ถูกโอนไป",
    ],
    examples: [
      "คู่สมรสต่างชาติต้องการจดทะเบียนในไทยและใช้ทะเบียนสมรสที่ประเทศตน",
      "ต้องการหย่าแต่ตกลงเรื่องบุตรและทรัพย์สินกันไม่ได้",
      "ฝ่ายหนึ่งไม่จ่ายค่าเลี้ยงดูบุตรตามที่ตกลง",
      "ต้องการรับรองบุตรเพื่อให้บุตรมีสิทธิตามกฎหมาย",
      "ต้องใช้เอกสารครอบครัวฉบับแปลรับรองยื่นต่อสถานทูต",
    ],
    docs: [
      "บัตรประชาชน ทะเบียนบ้าน หรือหนังสือเดินทางของทั้งสองฝ่าย",
      "ทะเบียนสมรสหรือทะเบียนหย่า (ถ้ามี)",
      "สูติบัตรบุตรและทะเบียนบ้านของบุตร",
      "หนังสือรับรองสถานะบุคคลจากประเทศของคู่สมรสต่างชาติ",
      "หลักฐานทรัพย์สินที่ได้มาระหว่างสมรส",
      "หลักฐานรายได้ของทั้งสองฝ่ายในกรณีค่าอุปการะเลี้ยงดู",
    ],
    steps: [
      "ปรึกษาเบื้องต้นเพื่อแยกว่าเรื่องใดจบที่สำนักทะเบียน เรื่องใดต้องขึ้นศาล",
      "ทนายสรุปทางเลือก ผลที่ตามมา และค่าบริการเป็นลายลักษณ์อักษร",
      "จัดเตรียมเอกสาร แปล และรับรองตามที่หน่วยงานปลายทางกำหนด",
      "เจรจาหรือทำบันทึกข้อตกลง หากตกลงกันได้",
      "ยื่นคำร้องหรือฟ้องต่อศาลเยาวชนและครอบครัวเมื่อจำเป็น",
      "ดำเนินการตามคำพิพากษา รวมถึงการบังคับคดีเรื่องค่าเลี้ยงดู",
    ],
    cautions: [
      {
        title: "หย่าแล้วแต่ไม่จัดการเรื่องทรัพย์สินให้ชัด",
        body: "การหย่าโดยไม่ระบุการแบ่งสินสมรสให้ครบทำให้ต้องกลับมาฟ้องกันใหม่ในภายหลัง",
      },
      {
        title: "ข้อตกลงเรื่องบุตรที่บังคับไม่ได้",
        body: "ข้อตกลงที่ไม่ผ่านรูปแบบตามกฎหมายอาจใช้บังคับไม่ได้เมื่ออีกฝ่ายไม่ปฏิบัติตาม",
      },
      {
        title: "เอกสารต่างประเทศไม่ผ่านการรับรองตามลำดับ",
        body: "เอกสารสถานะบุคคลจากต่างประเทศมักต้องผ่านการรับรองและแปลตามลำดับที่หน่วยงานไทยกำหนด หากลำดับผิดจะถูกตีกลับ",
      },
      {
        title: "ย้ายบุตรออกนอกประเทศโดยไม่มีอำนาจปกครองชัดเจน",
        body: "อาจเกิดปัญหาทางกฎหมายทั้งในไทยและปลายทาง ควรตรวจสิทธิให้ชัดก่อนเดินทาง",
      },
    ],
    handoff:
      "ถ้าไม่ต้องการเผชิญหน้ากับอีกฝ่ายโดยตรง ให้สำนักงานเป็นผู้ติดต่อ เจรจา และดำเนินการทางเอกสารแทนทั้งหมด ทนายจะอธิบายทางเลือกด้วยภาษาที่เข้าใจง่ายและรักษาความลับของลูกความอย่างเคร่งครัด",
    links: [
      { label: "บริการคดีครอบครัวและจดทะเบียนสมรส", to: "/family" },
      { label: "คำถามที่พบบ่อยเรื่องครอบครัว", to: "/family/questions" },
    ],
  },
  {
    slug: "estate",
    name: "มรดกและพันธุกรรม",
    tagline: "พินัยกรรม ผู้จัดการมรดก แบ่งมรดก และข้อพิพาททายาท",
    intro:
      "ดูแลงานมรดกทั้งก่อนและหลังการเสียชีวิต ตั้งแต่จัดทำพินัยกรรมให้ถูกรูปแบบตามกฎหมาย ยื่นคำร้องขอตั้งผู้จัดการมรดก รวบรวมและโอนทรัพย์มรดก ไปจนถึงคดีข้อพิพาทระหว่างทายาทและกรณีที่ทายาทหรือทรัพย์สินอยู่ต่างประเทศ",
    scope: [
      "จัดทำพินัยกรรมแบบเขียนเอง แบบมีพยาน และแบบทำต่อเจ้าพนักงาน",
      "ยื่นคำร้องขอตั้งผู้จัดการมรดกต่อศาล",
      "รวบรวมทรัพย์มรดก ตรวจหนี้สิน และจัดทำบัญชีทรัพย์",
      "โอนที่ดิน บัญชีธนาคาร หุ้น และยานพาหนะให้ทายาท",
      "คดีเพิกถอนพินัยกรรมและข้อพิพาทระหว่างทายาท",
      "งานมรดกที่มีทายาทหรือทรัพย์สินอยู่ต่างประเทศ",
    ],
    examples: [
      "เจ้าของทรัพย์ต้องการทำพินัยกรรมเพื่อป้องกันข้อพิพาทในอนาคต",
      "ธนาคารขอคำสั่งศาลตั้งผู้จัดการมรดกก่อนปล่อยเงินฝาก",
      "ที่ดินยังเป็นชื่อผู้เสียชีวิตและโอนไม่ได้",
      "ทายาทบางคนไม่ยินยอมให้แบ่งทรัพย์",
      "ทายาทอยู่ต่างประเทศ ต้องมอบอำนาจและรับรองเอกสารข้ามประเทศ",
    ],
    docs: [
      "ใบมรณบัตรของผู้ตาย",
      "ทะเบียนบ้านและบัตรประชาชนของผู้ตายและทายาท",
      "ทะเบียนสมรสหรือทะเบียนหย่าของผู้ตาย",
      "โฉนดที่ดิน สมุดบัญชีธนาคาร ทะเบียนรถ และหลักฐานทรัพย์สินอื่น",
      "พินัยกรรม (ถ้ามี)",
      "หนังสือยินยอมของทายาทที่ยินยอมให้ตั้งผู้จัดการมรดก",
    ],
    steps: [
      "ตรวจว่ามีพินัยกรรมหรือไม่ และไล่ลำดับทายาทตามกฎหมาย",
      "รวบรวมเอกสารทรัพย์สินและหนี้สินของผู้ตาย",
      "ยื่นคำร้องขอตั้งผู้จัดการมรดกต่อศาลที่มีเขตอำนาจ",
      "รับคำสั่งศาลและคัดสำเนาคำสั่งเพื่อใช้ติดต่อหน่วยงาน",
      "โอนทรัพย์มรดกที่กรมที่ดิน ธนาคาร และหน่วยงานที่เกี่ยวข้อง",
      "จัดทำบัญชีแบ่งทรัพย์ให้ทายาท หรือดำเนินคดีหากมีข้อพิพาท",
    ],
    cautions: [
      {
        title: "พินัยกรรมผิดรูปแบบ",
        body: "พินัยกรรมที่ไม่ครบองค์ประกอบตามกฎหมายอาจเสียเปล่าทั้งฉบับ ทำให้ทรัพย์ถูกแบ่งตามลำดับทายาทแทนเจตนาของผู้ทำ",
      },
      {
        title: "ปล่อยให้ที่ดินค้างชื่อผู้ตายนาน",
        body: "ยิ่งทิ้งไว้นาน ทายาทยิ่งกระจายและติดตามลงนามยากขึ้น ค่าใช้จ่ายและระยะเวลาก็เพิ่มตาม",
      },
      {
        title: "แบ่งทรัพย์กันเองก่อนมีคำสั่งศาล",
        body: "การถอนเงินหรือโอนทรัพย์ก่อนมีคำสั่งตั้งผู้จัดการมรดกอาจก่อความรับผิดต่อทายาทคนอื่น",
      },
      {
        title: "ลืมตรวจหนี้ของผู้ตาย",
        body: "กองมรดกต้องชำระหนี้ก่อนแบ่ง หากแบ่งไปแล้วเจ้าหนี้ยังมีสิทธิเรียกร้อง ทายาทจะเกิดปัญหาย้อนหลัง",
      },
    ],
    handoff:
      "ถ้าไม่อยากเดินศาล ธนาคาร และกรมที่ดินด้วยตัวเอง ให้สำนักงานรับดำเนินการทั้งกระบวนการแทน ตั้งแต่รวบรวมเอกสาร ยื่นคำร้อง ไปจนถึงโอนทรัพย์เข้าชื่อทายาท ทายาทที่อยู่ต่างประเทศทำผ่านการมอบอำนาจและการรับรองเอกสารได้",
    links: [
      { label: "บริการมรดกและพินัยกรรม", to: "/estate" },
      { label: "คำถามที่พบบ่อยเรื่องมรดก", to: "/estate/questions" },
    ],
  },
  {
    slug: "property",
    name: "ที่ดินและอสังหาริมทรัพย์",
    tagline: "ตรวจโฉนด สัญญาซื้อขาย เช่าระยะยาว โอนกรรมสิทธิ์ และข้อพิพาทที่ดิน",
    intro:
      "งานที่ดินคือเรื่องที่ตรวจก่อนจ่ายเงินคุ้มค่าที่สุด สำนักงานรับตรวจสอบสิทธิและภาระผูกพันในโฉนด ร่างและตรวจสัญญาจะซื้อจะขาย สัญญาเช่า สัญญาก่อสร้าง จัดการการโอนที่สำนักงานที่ดิน รวมถึงคดีข้อพิพาทเรื่องแนวเขต ทางภาระจำยอม และการครอบครองปรปักษ์",
    scope: [
      "ตรวจโฉนด ภาระจำยอม จำนอง อายัด และข้อจำกัดการใช้ที่ดิน",
      "ร่างและตรวจสัญญาจะซื้อจะขาย สัญญาเช่า และสัญญาก่อสร้าง",
      "จดทะเบียนสิทธิและนิติกรรมที่สำนักงานที่ดิน",
      "สัญญาเช่าระยะยาวและสิทธิเก็บกินสำหรับผู้เช่าต่างชาติ",
      "ข้อพิพาทแนวเขต ทางเข้าออก และการครอบครองปรปักษ์",
      "งานอาคารชุด นิติบุคคลหมู่บ้านจัดสรร และการเรียกเก็บค่าส่วนกลาง",
    ],
    examples: [
      "ก่อนวางมัดจำที่ดินหรือคอนโด ต้องการให้ทนายตรวจโฉนดและผู้ขาย",
      "ผู้ซื้อชาวต่างชาติต้องการเข้าใจสิทธิถือครองที่ทำได้ตามกฎหมาย",
      "เพื่อนบ้านปิดทางเข้าออกที่ใช้มานาน",
      "ผู้รับเหมาทิ้งงานหรือส่งมอบงานไม่ตรงแบบ",
      "โอนที่ดินระหว่างเครือญาติและต้องการทำให้ถูกต้อง",
    ],
    docs: [
      "สำเนาโฉนดที่ดินหรือหนังสือกรรมสิทธิ์ห้องชุด",
      "สัญญาจะซื้อจะขายหรือสัญญาเช่าฉบับที่ใช้อยู่",
      "บัตรประชาชนและทะเบียนบ้านของผู้ซื้อและผู้ขาย",
      "หลักฐานการชำระเงินและใบเสร็จมัดจำ",
      "ผังที่ตั้ง ภาพถ่ายที่ดิน และข้อมูลแนวเขตที่ทราบ",
      "หนังสือรับรองบริษัทหากคู่สัญญาเป็นนิติบุคคล",
    ],
    steps: [
      "ส่งสำเนาโฉนดและสัญญาให้ทีมงานตรวจเบื้องต้น",
      "ตรวจสารบัญจดทะเบียนและภาระผูกพันที่สำนักงานที่ดิน",
      "สรุปความเสี่ยงและแก้ไขข้อสัญญาให้เป็นธรรมก่อนวางเงิน",
      "นัดวันโอนและเตรียมเอกสารของทั้งสองฝ่ายให้ครบ",
      "ดำเนินการจดทะเบียนต่อเจ้าพนักงานที่ดินและตรวจเอกสารหลังโอน",
      "ดำเนินคดีหรือเจรจา หากพบข้อพิพาทเรื่องสิทธิในที่ดิน",
    ],
    cautions: [
      {
        title: "วางมัดจำก่อนตรวจโฉนด",
        body: "ภาระจำยอม จำนอง หรือข้อจำกัดการใช้ที่ดิน มักปรากฏหลังวางเงินแล้วและเรียกเงินคืนยาก",
      },
      {
        title: "โครงสร้างการถือครองที่ผิดกฎหมาย",
        body: "การให้คนไทยถือที่ดินแทนคนต่างชาติเป็นเรื่องที่มีความผิดตามกฎหมาย สำนักงานจะเสนอทางเลือกที่ทำได้จริง เช่น สิทธิการเช่าหรือสิทธิเก็บกิน",
      },
      {
        title: "สัญญาที่ไม่ระบุกำหนดโอนและเบี้ยปรับ",
        body: "เมื่อผู้ขายผิดนัด ผู้ซื้อจะไม่มีเครื่องมือบังคับที่ชัดเจน",
      },
      {
        title: "ไม่ตรวจผู้มีอำนาจลงนาม",
        body: "การลงนามโดยผู้ที่ไม่มีอำนาจหรือไม่มีหนังสือมอบอำนาจที่ถูกต้อง ทำให้นิติกรรมมีปัญหาภายหลัง",
      },
    ],
    handoff:
      "ให้สำนักงานตรวจให้ครบก่อนที่เงินจะออกจากบัญชี ทีมงานตรวจโฉนด แก้สัญญา เตรียมเอกสาร และไปสำนักงานที่ดินในวันโอนแทนหรือร่วมกับท่าน เพื่อให้จบด้วยเอกสารที่ถูกต้องในครั้งเดียว",
    links: [
      { label: "ตรวจสอบสถานะและทรัพย์สินก่อนลงทุน", to: "/due-diligence" },
      { label: "งานมรดกและที่ดินของทายาท", to: "/estate" },
    ],
  },
  {
    slug: "labour",
    name: "แรงงานและคนต่างชาติทำงานในไทย",
    tagline: "สัญญาจ้าง ระเบียบบริษัท เลิกจ้าง คดีแรงงาน วีซ่าและใบอนุญาตทำงาน",
    intro:
      "ให้บริการทั้งฝ่ายนายจ้างและลูกจ้าง ตั้งแต่ร่างสัญญาจ้างและข้อบังคับการทำงานให้สอดคล้องกฎหมายคุ้มครองแรงงาน วางขั้นตอนตักเตือนและเลิกจ้างให้ปลอดภัย ว่าความในศาลแรงงาน ไปจนถึงงานวีซ่าและใบอนุญาตทำงานของพนักงานต่างชาติที่ต้องทำคู่กันในทางปฏิบัติ",
    scope: [
      "ร่างสัญญาจ้าง ข้อบังคับการทำงาน และข้อตกลงรักษาความลับ",
      "ให้คำแนะนำเรื่องค่าจ้าง ค่าล่วงเวลา วันหยุด และสวัสดิการ",
      "ขั้นตอนตักเตือน โยกย้าย และเลิกจ้างที่ลดความเสี่ยงคดี",
      "เจรจาข้อพิพาทและว่าความในศาลแรงงาน",
      "วีซ่าทำงาน ใบอนุญาตทำงาน และการต่ออายุของพนักงานต่างชาติ",
      "งานรายงานต่อสำนักงานประกันสังคมและกรมการจัดหางาน",
    ],
    examples: [
      "บริษัทต้องการปรับโครงสร้างพนักงานโดยไม่ให้เกิดคดีเลิกจ้างไม่เป็นธรรม",
      "ลูกจ้างถูกเลิกจ้างโดยไม่ได้รับค่าชดเชยตามกฎหมาย",
      "พนักงานต่างชาติต้องต่อวีซ่าและใบอนุญาตทำงานให้ทันกำหนด",
      "ข้อพิพาทเรื่องค่าคอมมิชชันและค่าล่วงเวลา",
      "ต้องการวางข้อบังคับการทำงานฉบับใหม่ให้ทั้งองค์กร",
    ],
    docs: [
      "สัญญาจ้างและข้อบังคับการทำงานฉบับที่ใช้อยู่",
      "สลิปเงินเดือนและหลักฐานการจ่ายค่าจ้าง",
      "หนังสือตักเตือน บันทึกการประชุม และหนังสือเลิกจ้าง",
      "หนังสือเดินทาง วีซ่า และใบอนุญาตทำงานของพนักงานต่างชาติ",
      "หนังสือรับรองบริษัทและบัญชีรายชื่อผู้ถือหุ้น",
      "เอกสารประกันสังคมและทะเบียนลูกจ้าง",
    ],
    steps: [
      "ตรวจเอกสารการจ้างและลำดับเหตุการณ์ทั้งหมด",
      "ทนายประเมินความเสี่ยงตามกฎหมายคุ้มครองแรงงานและแนวคำพิพากษา",
      "วางขั้นตอนที่ควรทำ พร้อมร่างหนังสือที่ต้องใช้",
      "เจรจากับอีกฝ่ายหรือเข้าสู่กระบวนการไกล่เกลี่ยของพนักงานตรวจแรงงาน",
      "ยื่นคำฟ้องหรือคำให้การต่อศาลแรงงานเมื่อจำเป็น",
      "ดำเนินการตามผล และปรับเอกสารภายในเพื่อป้องกันกรณีเดิมซ้ำ",
    ],
    cautions: [
      {
        title: "เลิกจ้างด้วยวาจา",
        body: "การเลิกจ้างที่ไม่มีเอกสารและเหตุผลชัดเจนเป็นสาเหตุคดีแรงงานที่พบมากที่สุด",
      },
      {
        title: "เรียกลูกจ้างเขียนใบลาออกแทนการเลิกจ้าง",
        body: "หากลูกจ้างโต้แย้งภายหลัง ศาลพิจารณาข้อเท็จจริงประกอบ วิธีนี้มักเพิ่มความเสี่ยงมากกว่าลด",
      },
      {
        title: "จ้างพนักงานต่างชาติก่อนได้รับอนุญาตให้ทำงาน",
        body: "ทั้งนายจ้างและลูกจ้างมีความรับผิดตามกฎหมาย ควรจัดลำดับเอกสารให้ถูกต้องก่อนเริ่มงาน",
      },
      {
        title: "ข้อบังคับการทำงานที่ขัดกฎหมาย",
        body: "ข้อกำหนดที่ให้สิทธิน้อยกว่ากฎหมายกำหนดใช้บังคับไม่ได้ และอาจกลายเป็นหลักฐานเสียเปรียบ",
      },
    ],
    handoff:
      "ถ้าไม่ต้องการเสี่ยงเดินขั้นตอนผิดลำดับ ให้สำนักงานร่างเอกสาร วางขั้นตอน และเจรจาแทน ทั้งงานแรงงานและงานวีซ่า/ใบอนุญาตทำงานอยู่ในสำนักงานเดียว จึงจัดคู่กันได้ในเรื่องเดียว",
    links: [
      { label: "วีซ่าและใบอนุญาตทำงาน", to: "/visa" },
      { label: "งานคนต่างชาติและตรวจคนเข้าเมือง", to: "/immigration" },
    ],
  },
  {
    slug: "tax",
    name: "ภาษีอากรและบัญชี",
    tagline: "วางแผนภาษีตามกฎหมาย ยื่นแบบ ตรวจสอบ และอุทธรณ์การประเมิน",
    intro:
      "ทำงานร่วมกันระหว่างทนายและทีมบัญชี ดูแลตั้งแต่งานบัญชีรายเดือน ยื่นแบบภาษี วางโครงสร้างธุรกรรมให้ถูกต้องตามประมวลรัษฎากร ไปจนถึงการชี้แจงต่อกรมสรรพากรและการอุทธรณ์การประเมินภาษี",
    scope: [
      "จัดทำบัญชี ปิดงบ และยื่นแบบภาษีตามกำหนดเวลา",
      "จดทะเบียนภาษีมูลค่าเพิ่มและภาษีธุรกิจเฉพาะ",
      "ตรวจโครงสร้างธุรกรรมและสัญญาในมุมภาระภาษี",
      "ชี้แจงเอกสารเมื่อถูกเรียกตรวจ และเจรจากับเจ้าพนักงาน",
      "อุทธรณ์การประเมินและคดีภาษีในศาลภาษีอากร",
      "ภาษีที่เกี่ยวกับคนต่างชาติและการโอนเงินระหว่างประเทศ",
    ],
    examples: [
      "บริษัทได้รับหนังสือเชิญพบเจ้าพนักงานประเมิน",
      "ต้องการตรวจว่าค่าใช้จ่ายที่ลงบัญชีอยู่ถือเป็นรายจ่ายได้หรือไม่",
      "ขายอสังหาริมทรัพย์และต้องการทราบภาระภาษีที่แท้จริงก่อนโอน",
      "ชาวต่างชาติมีรายได้ทั้งในและนอกประเทศและต้องการยื่นให้ถูกต้อง",
      "ต้องการวางระบบเอกสารเพื่อลดความเสี่ยงถูกประเมินเพิ่ม",
    ],
    docs: [
      "งบการเงินและแบบยื่นภาษีย้อนหลังตามที่ทีมงานแจ้ง",
      "หนังสือรับรองบริษัทและทะเบียนภาษีมูลค่าเพิ่ม",
      "ใบกำกับภาษี ใบเสร็จ และเอกสารประกอบรายจ่าย",
      "สัญญาที่เกี่ยวข้องกับธุรกรรมที่ต้องการตรวจ",
      "หนังสือแจ้งหรือหมายเรียกจากกรมสรรพากร (ถ้ามี)",
      "รายการเดินบัญชีธนาคารที่เกี่ยวข้อง",
    ],
    steps: [
      "ส่งเอกสารทางบัญชีและหนังสือจากหน่วยงานให้ทีมงานตรวจ",
      "สรุปประเด็นภาษีที่มีความเสี่ยงและฐานกฎหมายที่เกี่ยวข้อง",
      "จัดชุดเอกสารชี้แจงและเตรียมคำอธิบายให้เจ้าพนักงาน",
      "เข้าพบหรือยื่นหนังสือชี้แจงแทนหรือร่วมกับลูกความ",
      "อุทธรณ์การประเมินภายในกำหนดเวลาเมื่อไม่เห็นด้วย",
      "ปรับระบบบัญชีและเอกสารเพื่อป้องกันประเด็นเดิมในปีถัดไป",
    ],
    cautions: [
      {
        title: "ปล่อยกำหนดอุทธรณ์ให้ผ่านไป",
        body: "การอุทธรณ์การประเมินมีกำหนดเวลาที่เคร่งครัด เลยกำหนดแล้วสิทธิโต้แย้งจะเสียไป",
      },
      {
        title: "เอกสารประกอบรายจ่ายไม่ครบ",
        body: "รายจ่ายที่ไม่มีเอกสารรองรับมักถูกตัดออก ส่งผลให้ภาษีที่ต้องชำระเพิ่มขึ้นพร้อมเบี้ยปรับเงินเพิ่ม",
      },
      {
        title: "แยกเงินส่วนตัวกับเงินบริษัทไม่ชัด",
        body: "การใช้บัญชีร่วมกันทำให้อธิบายที่มาของเงินยากเมื่อถูกตรวจ",
      },
      {
        title: "ทำโครงสร้างเพื่อเลี่ยงภาษี",
        body: "สำนักงานให้บริการวางแผนภาษีตามกรอบกฎหมายเท่านั้น และไม่รับงานที่มีลักษณะปิดบังข้อเท็จจริง",
      },
    ],
    handoff:
      "ถ้าไม่อยากตอบหนังสือจากหน่วยงานเอง ให้ทีมบัญชีและทนายของสำนักงานจัดชุดเอกสาร เขียนคำชี้แจง และเข้าพบเจ้าพนักงานแทน พร้อมวางระบบเอกสารให้ปีต่อไปตรวจง่ายขึ้น",
    links: [
      { label: "บริการบัญชีและภาษี", to: "/accounting" },
      { label: "คำถามที่พบบ่อยเรื่องภาษี", to: "/accounting/questions" },
    ],
  },
  {
    slug: "ip",
    name: "ทรัพย์สินทางปัญญา",
    tagline: "เครื่องหมายการค้า ลิขสิทธิ์ สิทธิบัตร สัญญาอนุญาตใช้สิทธิ และคดีละเมิด",
    intro:
      "ดูแลทรัพย์สินทางปัญญาตั้งแต่ตรวจความเหมือนคล้ายและยื่นจดทะเบียนเครื่องหมายการค้า แจ้งข้อมูลลิขสิทธิ์ ยื่นคำขอสิทธิบัตรและอนุสิทธิบัตร ร่างสัญญาอนุญาตให้ใช้สิทธิและโอนสิทธิ ไปจนถึงการดำเนินคดีกับผู้ละเมิดทั้งทางแพ่งและทางอาญา",
    scope: [
      "ตรวจความเหมือนคล้ายและยื่นจดทะเบียนเครื่องหมายการค้า",
      "แก้คำสั่งนายทะเบียนและอุทธรณ์คำสั่งปฏิเสธ",
      "แจ้งข้อมูลลิขสิทธิ์และวางระบบหลักฐานความเป็นเจ้าของ",
      "คำขอสิทธิบัตรการประดิษฐ์ อนุสิทธิบัตร และการออกแบบผลิตภัณฑ์",
      "สัญญาอนุญาตให้ใช้สิทธิ แฟรนไชส์ และโอนสิทธิ",
      "หนังสือเตือน การระงับสินค้าละเมิด และคดีในศาลทรัพย์สินทางปัญญา",
    ],
    examples: [
      "แบรนด์ใหม่ต้องการจดเครื่องหมายการค้าก่อนออกตลาด",
      "พบผู้อื่นขายสินค้าลอกเลียนแบรนด์ของเราออนไลน์",
      "ถูกกล่าวหาว่าละเมิดลิขสิทธิ์งานภาพหรือซอฟต์แวร์",
      "ต้องการทำสัญญาแฟรนไชส์ให้ผู้ร่วมลงทุน",
      "ต้องการเอกสารสิทธิฉบับแปลรับรองเพื่อยื่นต่างประเทศ",
    ],
    docs: [
      "ภาพเครื่องหมายการค้าหรือโลโก้ไฟล์ต้นฉบับ",
      "รายการสินค้าหรือบริการที่ต้องการคุ้มครอง",
      "หนังสือรับรองบริษัทหรือบัตรประชาชนของผู้ขอ",
      "หลักฐานการใช้งานจริง เช่น บรรจุภัณฑ์ เว็บไซต์ ใบเสร็จ",
      "หลักฐานการสร้างสรรค์งานและวันที่สร้าง",
      "หลักฐานการละเมิดที่พบ เช่น ภาพหน้าร้าน ลิงก์ ใบเสร็จซื้อของกลาง",
    ],
    steps: [
      "ตรวจความเหมือนคล้ายและประเมินโอกาสรับจดทะเบียน",
      "จัดกลุ่มสินค้า/บริการและร่างคำขอให้ครอบคลุมการใช้จริง",
      "ยื่นคำขอต่อกรมทรัพย์สินทางปัญญาและติดตามคำสั่ง",
      "ตอบข้อโต้แย้งหรืออุทธรณ์คำสั่งภายในกำหนดเวลา",
      "รับหนังสือสำคัญแสดงการจดทะเบียน และวางระบบต่ออายุ",
      "ดำเนินการกับผู้ละเมิด ทั้งหนังสือเตือน เจรจา และฟ้องคดี",
    ],
    cautions: [
      {
        title: "ใช้แบรนด์ก่อนตรวจความเหมือนคล้าย",
        body: "หากไปคล้ายเครื่องหมายที่จดไว้ก่อน ต้นทุนบรรจุภัณฑ์และการตลาดที่ลงไปอาจต้องทิ้งทั้งหมด",
      },
      {
        title: "จดในชื่อบุคคลแต่ใช้ในบริษัท",
        body: "ทำให้ความเป็นเจ้าของและสิทธิเรียกร้องไม่ตรงกัน มีปัญหาเมื่อเกิดข้อพิพาทหรือขายกิจการ",
      },
      {
        title: "ลืมกำหนดต่ออายุ",
        body: "เครื่องหมายการค้ามีอายุคุ้มครองและต้องต่ออายุตามกำหนด หากขาดไปสิทธิอาจสิ้นสุด",
      },
      {
        title: "เก็บหลักฐานการละเมิดไม่ถูกวิธี",
        body: "ภาพหน้าจอที่ไม่มีวันเวลาและที่มาชัดเจน มีน้ำหนักน้อยในชั้นศาล",
      },
    ],
    handoff:
      "ให้สำนักงานดูแลตั้งแต่ตรวจชื่อ ยื่นคำขอ ติดตามคำสั่ง จนถึงจัดการผู้ละเมิด ท่านโฟกัสที่การทำธุรกิจ ส่วนงานเอกสารและกำหนดเวลาตามกฎหมายเป็นหน้าที่ของทีมงาน",
    links: [
      { label: "บริการทรัพย์สินทางปัญญา", to: "/ip" },
      { label: "คำถามที่พบบ่อยเรื่อง IP", to: "/ip/questions" },
    ],
  },
  {
    slug: "notary-international",
    name: "Notary และเอกสารระหว่างประเทศ",
    tagline: "Notary Public แปลรับรอง กรมการกงสุล สถานทูต และเอกสารข้ามประเทศ",
    intro:
      "งานที่สำนักงานทำมาต่อเนื่องกว่า 15 ปี ครอบคลุมคำรับรองลายมือชื่อและเอกสารโดยทนายผู้ได้รับใบอนุญาต (Notarial Services Attorney) การแปลและรับรองคำแปล การรับรองนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ และการยื่นต่อสถานทูตปลายทาง รวมถึงหนังสือรับรองความประพฤติและเอกสารจากกระทรวงยุติธรรม",
    scope: [
      "รับรองลายมือชื่อ สำเนาเอกสาร คำสาบาน และหนังสือมอบอำนาจ",
      "แปลเอกสารและรับรองคำแปลตามรูปแบบที่ปลายทางกำหนด",
      "รับรองนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ",
      "ประสานการยื่นต่อสถานทูตหรือสถานกงสุลของประเทศปลายทาง",
      "หนังสือรับรองความประพฤติและเอกสารกระทรวงยุติธรรม",
      "ตรวจลำดับการรับรองเพื่อไม่ให้รับรองเกินความจำเป็น",
    ],
    examples: [
      "ใช้เอกสารเรียนต่อ ทำงาน หรือแต่งงานในต่างประเทศ",
      "มอบอำนาจให้ญาติในไทยดำเนินการแทนจากต่างประเทศ",
      "เอกสารบริษัทเพื่อเปิดบัญชีหรือจดทะเบียนสาขาต่างประเทศ",
      "เอกสารรับบุตรบุญธรรมหรือเอกสารสถานะบุคคลข้ามประเทศ",
      "เอกสารที่ถูกปลายทางตีกลับและต้องแก้ให้ถูกลำดับ",
    ],
    docs: [
      "เอกสารต้นฉบับหรือสำเนาที่หน่วยงานออกให้",
      "หนังสือเดินทางหรือบัตรประชาชนของผู้ลงนาม",
      "ชื่อหน่วยงานและประเทศปลายทางที่จะใช้เอกสาร",
      "แบบฟอร์มหรือข้อกำหนดที่ปลายทางส่งมาให้ (ถ้ามี)",
      "หนังสือรับรองบริษัทฉบับล่าสุด กรณีเอกสารนิติบุคคล",
      "ชื่อ-นามสกุลภาษาอังกฤษที่ต้องการให้ตรงกับหนังสือเดินทาง",
    ],
    steps: [
      "ส่งภาพเอกสารและระบุประเทศ/หน่วยงานปลายทาง",
      "ทีมงานตรวจลำดับการรับรองที่จำเป็นและแจ้งค่าบริการเป็นลายลักษณ์อักษร",
      "แปลเอกสารและตรวจการสะกดชื่อให้ตรงหนังสือเดินทาง",
      "ลงนามต่อหน้าทนายในกรณีที่เอกสารกำหนด",
      "ยื่นรับรองนิติกรณ์ที่กรมการกงสุล และยื่นสถานทูตเมื่อปลายทางกำหนด",
      "ส่งมอบเอกสารและเก็บสำเนาชุดอ้างอิงให้ลูกความ",
    ],
    cautions: [
      {
        title: "ประเทศไทยไม่ได้เป็นภาคีอนุสัญญา Apostille",
        body: "เอกสารเพื่อใช้ต่างประเทศโดยทั่วไปจึงผ่านการรับรองนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ และผ่านสถานทูตปลายทางเมื่อหน่วยงานผู้รับกำหนด ไม่ใช่การประทับ Apostille",
      },
      {
        title: "สะกดชื่อไม่ตรงหนังสือเดินทาง",
        body: "เป็นสาเหตุถูกตีกลับที่พบมากที่สุด ควรส่งหน้าหนังสือเดินทางมาให้ทีมงานตรวจก่อนแปล",
      },
      {
        title: "รับรองเกินความจำเป็น",
        body: "บางหน่วยงานต้องการเพียงคำรับรองของทนาย การยื่นทุกขั้นทำให้เสียเวลาและค่าใช้จ่ายโดยไม่จำเป็น",
      },
      {
        title: "ไม่ตรวจข้อกำหนดของหน่วยงานผู้รับ",
        body: "ข้อกำหนดต่างกันตามประเทศและประเภทเอกสาร และเปลี่ยนแปลงได้ ควรยืนยันกับปลายทางทุกครั้งก่อนเริ่ม",
      },
    ],
    handoff:
      "ส่งภาพเอกสารมาให้ทีมงานตรวจลำดับให้ครบก่อนเริ่ม แล้วให้สำนักงานเดินเรื่องแทนทั้งหมด ทั้งแปล รับรอง ยื่นกรมการกงสุล และประสานสถานทูต ท่านไม่ต้องเดินหลายหน่วยงานด้วยตัวเอง",
    links: [
      { label: "บริการ Notary Public", to: "/notary" },
      { label: "บริการแปลเอกสารทุกภาษา", to: "/translation" },
      { label: "รับรองกงสุลและสถานทูต", to: "/lgz" },
    ],
  },
  {
    slug: "pdpa",
    name: "PDPA และงานที่ปรึกษาองค์กร",
    tagline: "คุ้มครองข้อมูลส่วนบุคคล เอกสารกำกับดูแล และที่ปรึกษากฎหมายรายเดือน",
    intro:
      "วางระบบคุ้มครองข้อมูลส่วนบุคคลให้องค์กรใช้ได้จริง ทั้งนโยบายความเป็นส่วนตัว หนังสือให้ความยินยอม ข้อตกลงประมวลผลข้อมูล บันทึกรายการกิจกรรม และแนวปฏิบัติเมื่อเกิดเหตุข้อมูลรั่วไหล พร้อมบริการที่ปรึกษากฎหมายรายเดือนสำหรับองค์กรที่ต้องการทนายประจำแต่ยังไม่ต้องตั้งฝ่ายกฎหมายภายใน",
    scope: [
      "ตรวจกิจกรรมการเก็บและใช้ข้อมูลส่วนบุคคลของทั้งองค์กร",
      "ร่างนโยบายความเป็นส่วนตัว หนังสือยินยอม และประกาศที่เกี่ยวข้อง",
      "ข้อตกลงประมวลผลข้อมูลกับผู้ให้บริการภายนอก",
      "บันทึกรายการกิจกรรมการประมวลผลและแบบคำขอใช้สิทธิของเจ้าของข้อมูล",
      "แนวปฏิบัติและเอกสารรับมือเหตุข้อมูลรั่วไหล",
      "ที่ปรึกษากฎหมายรายเดือน ตรวจสัญญาและตอบข้อหารือประจำ",
    ],
    examples: [
      "เว็บไซต์และแอปเก็บข้อมูลลูกค้าและต้องมีเอกสารให้ครบตามกฎหมาย",
      "ฝ่ายบุคคลต้องการแบบหนังสือยินยอมสำหรับพนักงานและผู้สมัครงาน",
      "ได้รับคำขอให้ลบข้อมูลจากลูกค้าและไม่ทราบขั้นตอนที่ถูกต้อง",
      "ต้องการทนายประจำตรวจสัญญาที่เข้ามาทุกเดือน",
      "ต้องการอบรมพนักงานเรื่องการจัดการข้อมูลส่วนบุคคล",
    ],
    docs: [
      "รายการระบบและช่องทางที่เก็บข้อมูลลูกค้าหรือพนักงาน",
      "นโยบายและแบบฟอร์มที่ใช้อยู่ปัจจุบัน",
      "สัญญากับผู้ให้บริการภายนอกที่เข้าถึงข้อมูล",
      "โครงสร้างองค์กรและผู้รับผิดชอบข้อมูลในแต่ละฝ่าย",
      "ตัวอย่างข้อมูลที่จัดเก็บและระยะเวลาที่เก็บไว้",
      "บันทึกเหตุการณ์ข้อมูลรั่วไหลที่เคยเกิด (ถ้ามี)",
    ],
    steps: [
      "สัมภาษณ์แต่ละฝ่ายเพื่อจัดทำแผนผังการไหลของข้อมูล",
      "ประเมินช่องว่างระหว่างการปฏิบัติจริงกับข้อกำหนดตามกฎหมาย",
      "ร่างชุดเอกสารและแนวปฏิบัติที่องค์กรใช้ได้จริง",
      "อบรมผู้เกี่ยวข้องและวางผู้รับผิดชอบในแต่ละขั้น",
      "ทดสอบขั้นตอนคำขอใช้สิทธิและการรับมือเหตุรั่วไหล",
      "ทบทวนเอกสารเป็นรอบตามการเปลี่ยนแปลงของธุรกิจ",
    ],
    cautions: [
      {
        title: "คัดลอกนโยบายจากเว็บอื่นมาใช้",
        body: "นโยบายที่ไม่ตรงกับกิจกรรมจริงขององค์กร ใช้อ้างอิงไม่ได้เมื่อถูกตรวจสอบ และอาจกลายเป็นหลักฐานที่เสียเปรียบ",
      },
      {
        title: "เก็บข้อมูลไว้นานเกินจำเป็น",
        body: "ควรกำหนดระยะเวลาจัดเก็บและวิธีทำลายให้ชัดเจนตั้งแต่ต้น",
      },
      {
        title: "ไม่มีขั้นตอนรับคำขอใช้สิทธิ",
        body: "เมื่อมีคำขอเข้ามาจริงและตอบไม่ทันกำหนด องค์กรมีความเสี่ยงจากการไม่ปฏิบัติตามกฎหมาย",
      },
      {
        title: "ผู้ให้บริการภายนอกไม่มีข้อตกลงประมวลผล",
        body: "ความรับผิดยังอยู่กับองค์กรผู้ควบคุมข้อมูล การไม่มีข้อตกลงทำให้ควบคุมความเสี่ยงไม่ได้",
      },
    ],
    handoff:
      "ให้สำนักงานเข้าไปตรวจ วางระบบ และร่างเอกสารให้ครบชุด แล้วดูแลต่อเป็นที่ปรึกษารายเดือน องค์กรจึงมีทนายที่รู้บริบทธุรกิจของท่านคอยตรวจสัญญาและตอบข้อหารือได้ทันที",
    links: [
      { label: "บริการ PDPA และคุ้มครองข้อมูล", to: "/pdpa" },
      { label: "งานเอกสารราชการอื่น ๆ", to: "/misc-official" },
    ],
  },
];

export const PRACTICE_AREA_COUNT = PRACTICE_AREAS.length;

export function practiceArea(slug: string): PracticeArea | undefined {
  return PRACTICE_AREAS.find((a) => a.slug === slug);
}
